import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, finalize, Observable, switchMap, take, throwError } from 'rxjs';
import { CommandEnum } from '../../../definition/interface/enum';
import {
  GedDocumentEnum,
  PackTypeEnum,
  PrPackDocumentDto,
  PrPackDto,
} from '../../../definition/interface/Pack';
import { Pledge, PledgeRead } from '../../../definition/interface/Pledge';
import { SubscriptionFacadeService } from '../../../services/subscription-facade.service';
import { DOCUMENT_EDITOR_TOOLBAR_BUTTONS } from '../froala-editor/froala-editor.component';

/**
 * Séquençage V2 (parité legacy) — Un fragment HTML éditable d'un document
 * (couverture, page intermédiaire, corps/signature). Chaque fragment a son propre éditeur,
 * comme dans le funnel legacy, mais présenté en panneaux étiquetés repliables.
 */
interface FragmentRow {
  html: string;
  label: string;
  expanded: boolean;
}

/** Une ligne affichée pour un PrPackDocument. */
interface PackDocumentRow {
  packDocumentId: number;
  documentType: GedDocumentEnum;
  label: string;
  isReusable: boolean;
  signedAt: string | null;
  /** Fragments HTML éditables Froala (renseignés pour les docs NEW après GENERATE_PACK_*_HTML). */
  fragments: FragmentRow[] | null;
  /** Choix CGP pour les docs REUSABLE : `false` = Réutiliser (défaut), `true` = Regénérer. */
  regenerate: boolean;
  /** Accordion local : doc replié par défaut, clic header pour déplier. */
  expanded: boolean;
  /** doc optionnel (par ex. Convention RTO) — affiche la croix de suppression. */
  optional: boolean;
  /** id du PrGed déjà attaché (cas Pack BS : PDF pré-généré au funnel). */
  gedDocumentId: number | null;
  /** états de chargement du PDF preview inline. */
  loadingPdf: boolean;
  pdfSafeUrl: SafeResourceUrl | null;
  pdfError: boolean;
}

/** Section affichée pour un PrPack (Réglementaire / Adéquation / BS). */
interface PackSection {
  packType: PackTypeEnum;
  title: string;
  rows: PackDocumentRow[];
}

/**
 * Séquençage V2 — Écran de finalisation V2 affiché à la place de l'écran legacy
 * `document-verification` quand `pledge.legacyFlow === false`.
 *
 * <p>Flux :</p>
 * <ol>
 * <li><b>Au montage</b>, déclenche la cascade complète des commandes V2 — création des
 * {@code PrPack} (Réglementaire / Adéquation / BS) + génération du HTML éditable Froala
 * — en <strong>un seul</strong> {@code putPledge} qui les enchaîne côté backend.
 * <strong>L'écran reste sur un loader</strong> tant que la cascade n'est pas revenue —
 * cf. consigne PO « on enchaîne les commands puis on visualise ».</li>
 * <li>Quand le pledge revient peuplé, affiche les 3 packs côte à côte. Pour chaque
 * {@code PrPackDocument} :
 * <ul>
 * <li>NEW avec {@code editableHtmlFragments} → un panneau d'édition Froala par fragment
 * (couverture, contenu, signature) — parité legacy, présentation modernisée.</li>
 * <li>REUSABLE → message « signé le {date} » + radio Réutiliser (défaut) / Regénérer.</li>
 * </ul></li>
 * <li>Si le CGP clique Regénérer sur un doc, on refire immédiatement la cascade avec
 * {@code forceRegenerateDocumentTypes} pour basculer le doc REUSABLE → NEW + générer son
 * HTML, puis on ré-affiche. Comportement équivalent au bouton legacy
 * « Regénérer le pack réglementaire » mais granulaire par {@code documentType}.</li>
 * <li>Au clic Continuer : si des HTML ont été édités, on les envoie via
 * {@code SAVE_PACK_DOCUMENTS_HTML} (map packDocumentId → html), puis on avance d'une
 * étape dans le funnel via {@code next}.</li>
 * </ol>
 */
@UntilDestroy()
@Component({
  selector: 'pack-builder',
  templateUrl: './pack-builder.component.html',
  styleUrls: ['./pack-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PackBuilderComponent implements OnInit, OnDestroy {
  private _pledge!: Pledge;

  /** Le parent rafraîchit `pledge` après la cascade legacy : on rebâtit les sections à chaque set. */
  @Input()
  set pledge(value: Pledge) {
    this._pledge = value;
    if (value) {
      this.sections = this.buildSections(value);
      this.cdr.markForCheck();
    }
  }

  get pledge(): Pledge {
    return this._pledge;
  }

  @Output() next = new EventEmitter<Pledge>();
  @Output() prev = new EventEmitter<void>();

  loading = true;
  loadingMessage = 'Préparation des packs à signer…';
  toolbarButtons = DOCUMENT_EDITOR_TOOLBAR_BUTTONS;
  sections: PackSection[] = [];
  internalError: string | null = null;
  /** ids des docs optionnels retirés via la croix, à supprimer au Continuer. */
  private excludedPackDocumentIds: number[] = [];
  /**
   * Bug A — Liste cumulative des documentType regénérés. À chaque clic Regénérer, le type est
   * ajouté et la cascade est rejouée avec l'ensemble accumulé : sinon le backend recompose le
   * pack en repassant en REUSABLE tous les types absents de {@code forceRegenerateDocumentTypes},
   * ce qui annulait silencieusement les régénérations précédentes.
   */
  private forceRegenerateTypes = new Set<GedDocumentEnum>();

  /**
   * Cascade utilisée uniquement quand le CGP clique « Regénérer » sur un document REUSABLE :
   * on force le doc en NEW (REGLEMENTAIRE) + on régénère le HTML correspondant. ADEQUATION/BS
   * ne sont pas refaits ici — leur composition ne change pas avec le force-regenerate qui ne
   * cible que les docs réglementaires réutilisables.
   */
  private static readonly REGENERATE_REGLEMENTAIRE_CASCADE: CommandEnum[] = [
    CommandEnum.GENERATE_PACK_REGLEMENTAIRE,
    CommandEnum.GENERATE_PACK_REGLEMENTAIRE_HTML,
  ];

  constructor(
    private subscriptionFacade: SubscriptionFacadeService,
    private cdr: ChangeDetectorRef,
    private httpApiService: HttpApiService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    // Séquençage V2 — Le parent `document-verification` enchaîne déjà la chaîne legacy
    // (SAVE_OWNERS_DATA → FILL → VERIFY → COMPUTE_COMMISSION → PROCESS_UNIVERSIGN_PREVALIDATION
    // + GENERATE_SUBSCRIPTION_FILE → GENERATE_PACK_REGLEMENTAIRE + GENERATE_PACK_ADEQUATION
    // + GENERATE_PACK_BS + GENERATE_PACK_REGLEMENTAIRE_HTML + GENERATE_PACK_ADEQUATION_HTML
    // → PRE_VERIFICATION_EXTERNAL_API) avant de rendre cet écran. Le composant est passif :
    // il se contente de lire `pledge.packs` déjà peuplé (rebuild via le setter `pledge`).
    this.loading = false;
  }

  onConfirm(): void {
    // Séquençage V2 — Au Continuer : 1) REMOVE_PACK_DOCUMENTS pour supprimer les docs
    // optionnels retirés via la croix — fait en premier pour éviter de saver l'HTML
    // ou générer un preview PDF inutile sur un doc qui sera supprimé.
    // 2) SAVE_PACK_DOCUMENTS_HTML au cas où un doc a été édité.
    // 3) PREVIEW_PACK_PDFS (génère les PDFs preview + lie en PrGed) afin que l'étape suivante
    // `document-compliance` puisse afficher les previews PDF.
    const edits = this.collectHtmlEdits();
    const exclusions = [...this.excludedPackDocumentIds];
    this.loading = true;
    this.loadingMessage = 'Génération des documents…';
    this.cdr.markForCheck();
    // le Continuer est splitté en DEUX putPledge : exclusions + save HTML
    // (rapides, transaction courte) puis PREVIEW_PACK_PDFS seul. Grouper les trois enchaînait
    // ~8 générations Puppeteer + uploads S3 dans une seule requête HTTP → dépassement quasi
    // certain de l'idle timeout du proxy (le CGP voyait une erreur pendant que le backend
    // continuait). Même stratégie de découpage que la cascade d'arrivée (document-verification).
    this.subscriptionFacade
      .putPledge({
        ...this.pledge,
        parameters: {
          ...this.pledge.parameters,
          packDocumentsHtmlFragmentsToSave: edits,
          packDocumentIdsToExclude: exclusions.length > 0 ? exclusions : undefined,
        },
        commands: [
          ...(exclusions.length > 0 ? [CommandEnum.REMOVE_PACK_DOCUMENTS] : []),
          CommandEnum.SAVE_PACK_DOCUMENTS_HTML,
        ],
      })
      .pipe(
        untilDestroyed(this),
        switchMap((afterSave: PledgeRead) =>
          this.subscriptionFacade.putPledge({
            ...(afterSave as Pledge),
            commands: [CommandEnum.PREVIEW_PACK_PDFS],
          }),
        ),
        catchError(err => this.handleError(err)),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((result: PledgeRead) => {
        this.pledge = result as Pledge;
        this.next.emit(this.pledge);
      });
  }

  /**
   * Retire un doc optionnel du pack (par ex. Convention RTO). Suppression définitive
   * appliquée au Continuer via la commande REMOVE_PACK_DOCUMENTS.
   */
  onRemoveOptionalClick(section: PackSection, row: PackDocumentRow): void {
    if (!row.optional) {
      return;
    }
    this.excludedPackDocumentIds.push(row.packDocumentId);
    section.rows = section.rows.filter(r => r.packDocumentId !== row.packDocumentId);
    this.cdr.markForCheck();
  }

  onPrev(): void {
    this.prev.emit();
  }

  onRegenerateClick(row: PackDocumentRow): void {
    row.regenerate = true;
    this.forceRegenerateTypes.add(row.documentType);
    this.runCascade(
      Array.from(this.forceRegenerateTypes),
      `Régénération du document « ${row.label} »…`,
      // sans sauvegarde préalable, la reconstruction des sections après la
      // cascade écrasait silencieusement les éditions Froala en cours sur les AUTRES documents.
      this.collectHtmlEdits(),
    );
  }

  onKeepClick(row: PackDocumentRow): void {
    row.regenerate = false;
    this.forceRegenerateTypes.delete(row.documentType);
  }

  toggleExpand(row: PackDocumentRow): void {
    row.expanded = !row.expanded;
    // lazy load du PDF preview au premier dépliage (cas Pack BS : pas
    // d'editableHtml, mais un PrGed déjà attaché via PackBsBuildingService).
    if (
      row.expanded &&
      row.gedDocumentId != null &&
      row.fragments == null &&
      !row.pdfSafeUrl &&
      !row.loadingPdf
    ) {
      this.loadPdfPreview(row);
    }
  }

  /** object URLs créées pour les previews PDF, révoquées au ngOnDestroy. */
  private createdObjectUrls: string[] = [];

  ngOnDestroy(): void {
    this.createdObjectUrls.forEach(url => window.URL.revokeObjectURL(url));
    this.createdObjectUrls = [];
  }

  private loadPdfPreview(row: PackDocumentRow): void {
    if (!row.gedDocumentId || !this.pledge?.accountId) {
      return;
    }
    row.loadingPdf = true;
    row.pdfError = false;
    this.cdr.markForCheck();

    const url = `/accounts/${this.pledge.accountId}/documents/${row.gedDocumentId}`;
    this.httpApiService
      .getWithOptions(url, { responseType: 'arraybuffer' })
      .pipe(
        take(1),
        catchError((err: HttpErrorResponse) => {
          row.pdfError = true;
          row.loadingPdf = false;
          this.cdr.markForCheck();
          console.error(err);
          return throwError(err);
        }),
      )
      .subscribe((content: ArrayBuffer) => {
        const blob = new Blob([content], { type: 'application/pdf' });
        const dataUrl = window.URL.createObjectURL(blob);
        // chaque blob PDF restait retenu pour la vie de l'onglet (fuite
        // mémoire) : on tracke les URLs et on les révoque au destroy.
        this.createdObjectUrls.push(dataUrl);
        row.pdfSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl);
        row.loadingPdf = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Fire le full cascade V2 ({@link #INITIAL_CASCADE} + éventuels documents à forcer en NEW).
   * Affiche le loader pendant l'aller-retour et reconstruit les sections quand le pledge revient.
   */
  private runCascade(
    forceRegenerateDocumentTypes: GedDocumentEnum[],
    message: string,
    pendingHtmlEdits?: Record<number, string[]>,
  ): void {
    this.loading = true;
    this.loadingMessage = message;
    this.internalError = null;
    this.cdr.markForCheck();

    this.subscriptionFacade
      .putPledge({
        ...this.pledge,
        parameters: {
          ...this.pledge.parameters,
          forceRegenerateDocumentTypes,
          // persiste les éditions Froala en cours AVANT la régénération
          // (SAVE_PACK_DOCUMENTS_HTML en tête de cascade) pour ne pas les perdre au rebuild.
          packDocumentsHtmlFragmentsToSave: pendingHtmlEdits,
        },
        commands: [
          ...(pendingHtmlEdits && Object.keys(pendingHtmlEdits).length > 0
            ? [CommandEnum.SAVE_PACK_DOCUMENTS_HTML]
            : []),
          ...PackBuilderComponent.REGENERATE_REGLEMENTAIRE_CASCADE,
        ],
      })
      .pipe(
        untilDestroyed(this),
        catchError(err => this.handleError(err)),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((result: PledgeRead) => {
        this.pledge = result as Pledge;
        this.sections = this.buildSections(this.pledge);
        // markForCheck explicite : composant OnPush, le next mute sections.
        this.cdr.markForCheck();
      });
  }

  private handleError(err: unknown): Observable<never> {
    this.internalError =
      'Une erreur interne est survenue lors de la préparation des packs. Veuillez contacter le support technique.';
    return throwError(() => err);
  }

  private buildSections(pledge: Pledge): PackSection[] {
    const packs = pledge.packs ?? [];
    return [
      PackTypeEnum.REGLEMENTAIRE,
      PackTypeEnum.ADEQUATION,
      PackTypeEnum.BS,
    ]
      .map(type => this.buildSection(type, packs))
      .filter((section): section is PackSection => section !== null);
  }

  private buildSection(
    type: PackTypeEnum,
    packs: PrPackDto[],
  ): PackSection | null {
    const pack = packs.find(p => p.type === type);
    if (!pack || (pack.documents ?? []).length === 0) {
      return null;
    }
    return {
      packType: type,
      title: PackBuilderComponent.packLabel(type),
      rows: pack.documents.map(doc => this.toRow(doc)),
    };
  }

  private toRow(doc: PrPackDocumentDto): PackDocumentRow {
    return {
      packDocumentId: doc.id!,
      documentType: doc.documentType,
      // Titres par variant — « RCDA — Versements programmés », « RCDA — {support} »…
      // posés par le backend ; fallback sur le label générique du type.
      label: doc.title || PackBuilderComponent.documentLabel(doc.documentType),
      // Un doc déjà signé (quelle que soit la source : réutilisé depuis un PrPackDocument,
      // depuis un PrGed granulaire, ou depuis un pack juridique fusionné legacy) est en
      // lecture seule → affichage « signé le… » + choix Réutiliser/Regénérer. Les sources GED
      // n'ont pas de reusedFromPackDocumentId, d'où le test sur signedAt. Un doc réutilisé
      // encore EN COURS de signature (Source 1, signedAt null) reste couvert par le 2ᵉ terme.
      isReusable:
        doc.signedAt != null ||
        (doc.gedDocumentId != null && doc.reusedFromPackDocumentId != null),
      signedAt: doc.signedAt ?? null,
      fragments: PackBuilderComponent.toFragmentRows(doc.editableHtmlFragments),
      regenerate: false,
      expanded: false,
      optional: doc.optional === true,
      gedDocumentId: doc.gedDocumentId ?? null,
      loadingPdf: false,
      pdfSafeUrl: null,
      pdfError: false,
    };
  }

  /**
   * Séquençage V2 — Construit les panneaux d'édition par fragment, avec un libellé
   * compréhensible par le CGP : « Page de couverture », « Contenu (page N) », « Contenu & signature ».
   * Document mono-fragment : un seul panneau « Contenu du document », déplié d'office.
   */
  private static toFragmentRows(
    fragments: string[] | null | undefined,
  ): FragmentRow[] | null {
    if (fragments == null || fragments.length === 0) {
      return null;
    }
    if (fragments.length === 1) {
      return [{ html: fragments[0], label: 'Contenu du document', expanded: true }];
    }
    return fragments.map((html, i) => ({
      html,
      label:
        i === 0
          ? 'Page de couverture'
          : i === fragments.length - 1
            ? 'Contenu & signature'
            : fragments.length > 3
              ? `Contenu (partie ${i})`
              : 'Contenu',
      // Le contenu principal (dernier fragment) est ouvert d'office ; couverture et pages
      // intermédiaires repliées mais visibles et éditables en un clic.
      expanded: i === fragments.length - 1,
    }));
  }

  toggleFragment(fragment: FragmentRow): void {
    fragment.expanded = !fragment.expanded;
  }

  /** Aplatit toutes les sections en une map packDocumentId → fragments HTML édités. */
  private collectHtmlEdits(): Record<number, string[]> {
    const map: Record<number, string[]> = {};
    for (const section of this.sections) {
      for (const row of section.rows) {
        if (!row.isReusable && row.fragments != null && row.fragments.length > 0) {
          map[row.packDocumentId] = row.fragments.map(f => f.html);
        }
      }
    }
    return map;
  }

  private static packLabel(type: PackTypeEnum): string {
    switch (type) {
      case PackTypeEnum.REGLEMENTAIRE:
        return 'Pack Réglementaire';
      case PackTypeEnum.ADEQUATION:
        return 'Pack Adéquation';
      case PackTypeEnum.BS:
        return 'Pack BS';
    }
  }

  private static documentLabel(type: GedDocumentEnum): string {
    switch (type) {
      case GedDocumentEnum.DOCUMENT_ENTREE_EN_RELATION:
        return "Document d'Entrée en Relation";
      case GedDocumentEnum.LETTRE_DE_MISSION:
        return 'Lettre de mission';
      case GedDocumentEnum.FICHE_CONNAISSANCE_CLIENT:
        return 'Fiche de connaissance client';
      case GedDocumentEnum.MANDAT_IAS:
        return 'Mandat IAS';
      case GedDocumentEnum.RCDA:
        return 'RCDA';
      case GedDocumentEnum.CONVENTION_RTO:
        return 'Convention RTO';
      case GedDocumentEnum.ANNEXE_LETTRE_DE_MISSION:
        return 'Annexe lettre de mission';
      case GedDocumentEnum.ANNEXE_PRESENTATION_SUPPORTS_ALLOCATION:
        return "Annexe présentation supports d'allocation";
      case GedDocumentEnum.BULLETIN_DE_SOUSCRIPTION:
        return 'Bulletin de souscription';
      case GedDocumentEnum.AVENANT:
        return 'Avenant';
      case GedDocumentEnum.PACK_JURIDIC_CIF:
        return 'Pack réglementaire - CIF';
      case GedDocumentEnum.PACK_JURIDIC_IAS:
        return 'Pack réglementaire - IAS';
      default:
        return String(type);
    }
  }
}
